import './shop_ownerships.scss';

import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import React from 'react';
import { Avatar } from '../../components/Avatar/Avatar';
import { Box } from '../../components/Box/box';
import { useSmartConfirmation } from '../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Icon } from '../../components/Icon/Icon';
import { useModalPresenter } from '../../components/Modal/PresentedModal';
import { Spinner } from '../../components/Spinner/Spinner';
import { Stack } from '../../components/Stack/Stack';
import { graphql } from '../../generated';
import { t } from '../../locales/translate';
import { useQueryHandler } from '../../model/graphql';
import { CreateShopOwnership } from './create_shop_ownership';

const SHOP_OWNERSHIP = graphql(`
  query ShopOwnerships($id: ID!) {
    shop(id: $id) {
      id
      slug
      name

      shopOwnerships {
        nodes {
          id
          role
          company {
            id
            name
            slug
            icon {
              fileType
              id
              ... on Image {
                url(width: 80)
              }
            }
          }

          permissions {
            id
            destroy
          }
        }

        permissions {
          id
          create
        }
      }

      permissions {
        id
        updateShopOwnership
      }
    }
  }
`);

const DESTROY_SHOP_OWNERSHIP = graphql(`
  mutation DestroyShopOwnership($ownershipId: ID!) {
    destroyShopOwnership(input: { id: $ownershipId }) {
      clientMutationId
    }
  }
`);

interface Props {
  shopId: string;
}

export function ShopOwnerships({ shopId }: Props): JSX.Element {
  const { data, loading } = useQueryHandler(SHOP_OWNERSHIP, { variables: { id: shopId } });
  const [deleteOwnership] = useMutation(DESTROY_SHOP_OWNERSHIP, { refetchQueries: [SHOP_OWNERSHIP] });
  const modal = useModalPresenter();
  const shopOwnerships = data?.shop?.shopOwnerships.nodes;
  const confirmDelete = useSmartConfirmation();

  return (
    <div className="shop-ownerships">
      <div className="shop-ownerships__companies">
        {loading ? (
          <Box padding={{ top: 5 }}>
            <Stack align="center">
              <Spinner />
            </Stack>
          </Box>
        ) : (
          shopOwnerships && (
            <ul
              className={classNames('shop-ownerships__list', {
                'shop-ownerships__list--empty': shopOwnerships.length === 0,
              })}
            >
              {shopOwnerships.map((item, key) => {
                return (
                  <li key={key} className="shop-ownerships__list-item">
                    <Avatar
                      name={item.company.name || item.company.slug}
                      image={
                        item.company.icon && item.company.icon.__typename === 'Image'
                          ? item.company.icon.url
                          : undefined
                      }
                    />
                    <div className="shop-ownerships__details">
                      <div className="shop-ownerships__role">{t.model.role[item.role]}</div>
                      <div className="shop-ownerships__name">{item.company.name}</div>
                    </div>
                    {item.permissions.destroy && (
                      <div className="shop-ownerships__delete">
                        <Icon
                          name="trash--sm"
                          onClick={() =>
                            confirmDelete({
                              title: t.model.permissions.deleteTitle,
                              message: t.model.permissions.deleteConfirmation,
                              action: async () => {
                                await deleteOwnership({ variables: { ownershipId: item.id } });
                              },
                            })
                          }
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )
        )}
      </div>
      {data?.shop?.shopOwnerships.permissions.create && (
        <Icon
          name="plus"
          onClick={() => modal.present(CreateShopOwnership, { shopId })}
          className="shop-ownerships__add-new-icon"
          color="var(--color-tint)"
        />
      )}
    </div>
  );
}
